import React, { useRef, useState } from 'react';
import './MoodBoard.css';

/**
 * MoodBoard — composites selected designs into a shareable canvas collage.
 * Layouts: 2 items → side-by-side, 3 → 2+1, 4 → 2×2, 5-6 → 3+bottom row, 7-9 → 3×3 (truncated to 9)
 */

const CANVAS_SIZE = 1080; // square output
const PADDING = 12;
const LABEL_HEIGHT = 28;

function getLayout(count) {
  const n = Math.min(count, 9);
  if (n === 1) return [[1, 1]]; // cols, rows
  if (n === 2) return [[2, 1]];
  if (n === 3) return [[2, 1], [1, 1]]; // row1: 2 cols, row2: 1 col  — returned as rows
  if (n === 4) return [[2, 2]]; // 2x2
  if (n <= 6) return [[3, 2]];
  return [[3, 3]];
}

async function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    // Add cache-busting only if not a data URL
    if (url.startsWith('http')) {
      img.src = url.includes('?') ? url : `${url}?t=${Date.now()}`;
    } else {
      img.src = url;
    }
  });
}

async function buildCollage(designs) {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  const n = Math.min(designs.length, 9);
  const cols = n <= 2 ? n : n <= 4 ? 2 : 3;
  const rows = Math.ceil(n / cols);

  const inner = CANVAS_SIZE - PADDING * 2;
  const cellW = (inner - PADDING * (cols - 1)) / cols;
  const cellH = (inner - PADDING * (rows - 1) - LABEL_HEIGHT * rows) / rows;

  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = PADDING + col * (cellW + PADDING);
    const y = PADDING + row * (cellH + LABEL_HEIGHT + PADDING);

    // Draw image cell
    try {
      const img = await loadImage(designs[i].image_url);
      // Draw rounded-rect clip
      const radius = 8;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + cellW - radius, y);
      ctx.quadraticCurveTo(x + cellW, y, x + cellW, y + radius);
      ctx.lineTo(x + cellW, y + cellH - radius);
      ctx.quadraticCurveTo(x + cellW, y + cellH, x + cellW - radius, y + cellH);
      ctx.lineTo(x + radius, y + cellH);
      ctx.quadraticCurveTo(x, y + cellH, x, y + cellH - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.clip();

      // Cover-fit
      const scale = Math.max(cellW / img.width, cellH / img.height);
      const sw = cellW / scale;
      const sh = cellH / scale;
      const sx = (img.width - sw) / 2;
      const sy = (img.height - sh) / 2;
      ctx.drawImage(img, sx, sy, sw, sh, x, y, cellW, cellH);
      ctx.restore();
    } catch {
      // Fallback: colored rect
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(x, y, cellW, cellH);
    }

    // Label strip
    const labelY = y + cellH;
    ctx.fillStyle = 'rgba(138, 43, 226, 0.85)';
    ctx.fillRect(x, labelY, cellW, LABEL_HEIGHT);
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${Math.round(LABEL_HEIGHT * 0.45)}px -apple-system, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    const maxChars = Math.floor(cellW / (LABEL_HEIGHT * 0.25));
    const label = designs[i].prompt
      ? (designs[i].prompt.length > maxChars
        ? designs[i].prompt.slice(0, maxChars - 1) + '…'
        : designs[i].prompt)
      : 'FashionAI Design';
    ctx.fillText(label, x + cellW / 2, labelY + LABEL_HEIGHT / 2);
  }

  // Branding watermark
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.font = `bold 20px -apple-system, sans-serif`;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'right';
  ctx.fillText('✨ FashionAI', CANVAS_SIZE - PADDING, CANVAS_SIZE - PADDING / 2);

  return canvas;
}

export default function MoodBoard({ designs, onClose }) {
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const downloadRef = useRef(null);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const canvas = await buildCollage(designs);
      const url = canvas.toDataURL('image/png');
      setPreviewUrl(url);
    } catch (err) {
      console.error(err);
      setError('Failed to build collage. Some images may be blocked by CORS.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `fashionai-moodboard-${Date.now()}.png`;
    a.click();
  };

  return (
    <div className="moodboard-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="moodboard-modal">
        <button className="moodboard-close" onClick={onClose} aria-label="Close">✕</button>
        <h2 className="moodboard-title">🎨 Mood Board</h2>
        <p className="moodboard-subtitle">
          {designs.length} design{designs.length !== 1 ? 's' : ''} selected · Compose into a shareable collage
        </p>

        {/* Thumb strip */}
        <div className="moodboard-thumbs">
          {designs.slice(0, 9).map((d, i) => (
            <div key={d.id} className="moodboard-thumb">
              <img src={d.image_url} alt={d.prompt} />
              <span className="moodboard-thumb-num">{i + 1}</span>
            </div>
          ))}
          {designs.length > 9 && (
            <div className="moodboard-thumb moodboard-thumb-more">+{designs.length - 9}</div>
          )}
        </div>

        {!previewUrl ? (
          <button
            className="moodboard-gen-btn"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <span className="moodboard-spinner">⏳ Composing…</span>
            ) : (
              '🖼️ Create Mood Board'
            )}
          </button>
        ) : (
          <div className="moodboard-preview-wrap">
            <img className="moodboard-preview" src={previewUrl} alt="Mood board preview" />
            <div className="moodboard-actions">
              <button className="moodboard-dl-btn" onClick={handleDownload}>⬇️ Download PNG</button>
              <button className="moodboard-regen-btn" onClick={() => setPreviewUrl(null)}>↩️ Redo</button>
            </div>
          </div>
        )}

        {error && <p className="moodboard-error">{error}</p>}
      </div>
    </div>
  );
}
